import { GroupCluster } from '@amzn/aws-hammerstone-exposed-restful-service-typescript-client/clients/hammerstoneexposedrestfulservicelambda';
import React, { useMemo } from 'react';
import { fetchClustersByGroup } from 'src/data/api/fetchFromAPI';
import { useClusters, useGroupName } from 'src/data/redux';
import { useConditionalEffect } from 'src/commons';
import { DisplayMode, iSelectContent, ResourcePath, ResourceType } from '../helpers/content/contentInterfaces';
import Content from '../helpers/content/';
import { SelectProps } from '@amzn/awsui-components-console';

/**
 * Written based on the logic of get_cluster_permissions in manageactivity_controller.rb
 * https://code.amazon.com/packages/AWSDWHammerstoneWebsite/blobs/6a73ed521ebcb835da97ffb1b4cd6a7ccea3b662/--/rails-root/app/controllers/manageactivity_controller.rb#L2610
 */
export function clusterHasAccess(cluster: GroupCluster, access: 'READ' | 'WRITE'): boolean {
	switch (access) {
		case 'WRITE':
			return ['WRITE', 'ADMIN'].includes(cluster.accessType);
		case 'READ':
			return ['READ', 'WRITE', 'ADMIN'].includes(cluster.accessType);
	}
}

function clustersToOptions(
	clusters: GroupCluster[],
	access: 'READ' | 'WRITE',
	onlyShowEnabled?: boolean
): SelectProps.Options {
	// Only return clusters with the correct level of access
	const clusterNameSet = new Set();
	const filteredClusters = (clusters ?? [])
		.filter((cluster) => clusterHasAccess(cluster, access))
		// If onlyShowEnabled is true, filter for clusters with the appropriate status
		.filter(({ status }) => !onlyShowEnabled || status === 'ENABLED')
		// Ensure no duplicate cluster names are returned
		.filter(({ clusterName }) => !clusterNameSet.has(clusterName) && clusterNameSet.add(clusterName));

	// Sort clusters by name, alphabetically
	const sortedClusters = filteredClusters.sort((a, b) => a.clusterName.localeCompare(b.clusterName));

	// Map each cluster name to a dropdown option
	return sortedClusters.map((cluster) => ({
		label: cluster.clusterName,
		value: cluster.clusterName,
		// Make more human "Status" instead of screaming "STATUS"
		labelTag: cluster.status.charAt(0).toUpperCase() + cluster.status.slice(1).toLowerCase(),
		description: `${cluster.leaderNodeHostname}:${cluster.databasePort}/${cluster.databaseName}`,
		disabled: cluster.status === 'DELETED',
	}));
}

export default function ClusterSelectContent<
	RType extends keyof ResourceType,
	DisableOnPaths extends ResourcePath<RType>[] = []
>(
	props: Omit<iSelectContent<RType, DisableOnPaths>, 'options'> & {
		access: 'READ' | 'WRITE';
		onlyShowEnabled?: boolean;
	}
) {
	const { access, onlyShowEnabled } = props;
	const groupName = useGroupName();
	useConditionalEffect(() => {
		fetchClustersByGroup({ groupName }, true);
	}, [groupName]);

	const { data: clusters, fetching } = useClusters(groupName);

	const clusterOptions = useMemo(
		() => clustersToOptions(clusters, access, onlyShowEnabled),
		[clusters, access, onlyShowEnabled]
	);

	// TODO: Eventually add refresh button adjacent to select to fetch the options again, and all cooldown to prevent spamming <Button iconName="refresh" variant="icon-inline" disabled={cooldown} loading={fetching} onClick={fetch...} />
	return (
		<Content.Select
			infoHelpPanel="ClusterManagement"
			{...props}
			options={clusterOptions}
			filteringType="auto"
			// When loading clusters, communicate that to customers by setting component to loading mode
			mode={fetching ? DisplayMode.Loading : props.mode}
		/>
	);
}
